import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

interface VelocityTrendChartProps {
  data: Array<{
    month: string;
    velocity: number;
    target_velocity: number;
    workshops_completed: number;
  }>;
  loading?: boolean;
}

const VelocityTrendChart: React.FC<VelocityTrendChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Velocity Trend</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Velocity Trend</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No velocity data available
        </div>
      </div>
    );
  }

  const formatMonth = (month: string) => {
    const date = new Date(month);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const averageVelocity = data.reduce((sum, item) => sum + item.velocity, 0) / data.length;
  const averageTarget = data.reduce((sum, item) => sum + item.target_velocity, 0) / data.length;
  const performanceRatio = averageVelocity / averageTarget;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Velocity Trend</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatMonth}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={[0, 'dataMax + 2']}
            />
            <Tooltip 
              formatter={(value: number, name: string) => [
                name === 'velocity' ? `${value.toFixed(1)} workshops/month` : 
                name === 'target_velocity' ? `${value.toFixed(1)} target` :
                `${value} workshops`,
                name === 'velocity' ? 'Actual Velocity' :
                name === 'target_velocity' ? 'Target Velocity' : 'Completed'
              ]}
              labelFormatter={(label) => `Month: ${formatMonth(label)}`}
            />
            <Area
              type="monotone"
              dataKey="target_velocity"
              stroke="#e5e7eb"
              fill="#f3f4f6"
              strokeWidth={2}
              strokeDasharray="5 5"
            />
            <Area
              type="monotone"
              dataKey="velocity"
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.3}
              strokeWidth={3}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-3 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-1">Average Velocity</h4>
          <p className="text-2xl font-bold text-blue-600">{averageVelocity.toFixed(1)}</p>
          <p className="text-sm text-blue-700">workshops/month</p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-1">Target Velocity</h4>
          <p className="text-2xl font-bold text-gray-600">{averageTarget.toFixed(1)}</p>
          <p className="text-sm text-gray-700">workshops/month</p>
        </div>
        <div className={`p-3 rounded-lg ${performanceRatio >= 1 ? 'bg-green-50' : 'bg-red-50'}`}>
          <h4 className={`font-medium mb-1 ${performanceRatio >= 1 ? 'text-green-900' : 'text-red-900'}`}>
            Performance
          </h4>
          <p className={`text-2xl font-bold ${performanceRatio >= 1 ? 'text-green-600' : 'text-red-600'}`}>
            {(performanceRatio * 100).toFixed(0)}%
          </p>
          <p className={`text-sm ${performanceRatio >= 1 ? 'text-green-700' : 'text-red-700'}`}>
            vs target
          </p>
        </div>
      </div>
      <div className="mt-4">
        <h4 className="font-medium text-gray-900 mb-2">Recent Performance</h4>
        <div className="space-y-1">
          {data.slice(-3).map((item, index) => (
            <div key={item.month} className="flex items-center justify-between text-sm">
              <span>{formatMonth(item.month)}</span>
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">{item.workshops_completed} completed</span>
                <span className={`font-medium ${item.velocity >= item.target_velocity ? 'text-green-600' : 'text-red-600'}`}>
                  {item.velocity.toFixed(1)} velocity
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VelocityTrendChart;


